import React from "react";
import { Typography, Box, Skeleton } from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";

interface DistributionData {
  name: string;
  value: number;
}

interface DistributionChartProps {
  data: DistributionData[] | undefined;
  loading: boolean;
  title: string;
  maxItems?: number;
}

const COLORS = [
  "#1976d2",
  "#2e7d32",
  "#ed6c02",
  "#9c27b0",
  "#d32f2f",
  "#0288d1",
  "#388e3c",
  "#f57c00",
  "#7b1fa2",
  "#c62828",
];

export default function DistributionChart({
  data,
  loading,
  title,
  maxItems = 10,
}: DistributionChartProps) {
  // Pegar top N itens
  const topData = React.useMemo(() => {
    if (!data) return [];
    
    const sorted = [...data].sort((a, b) => b.value - a.value);
    const top = sorted.slice(0, maxItems);
    
    // Se houver mais itens, agrupar o resto em "Outros"
    if (sorted.length > maxItems) {
      const others = sorted.slice(maxItems);
      const othersTotal = others.reduce((sum, item) => sum + item.value, 0);
      top.push({ name: "Outros", value: othersTotal });
    }
    
    return top;
  }, [data, maxItems]);

  // Calcular percentuais
  const total = React.useMemo(() => {
    return topData.reduce((sum, item) => sum + item.value, 0);
  }, [topData]);

  // Custom label
  const renderLabel = (entry: any) => {
    const percentage = ((entry.value / total) * 100).toFixed(1);
    return `${percentage}%`;
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage = ((data.value / total) * 100).toFixed(1);
      return (
        <Box
          sx={{
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: 1,
            p: 1.5,
            boxShadow: 2,
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            {data.name}
          </Typography>
          <Typography variant="body2" color="primary">
            Quantidade: {data.value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Percentual: {percentage}%
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width="40%" height={32} />
        <Skeleton variant="circular" width={250} height={250} sx={{ mt: 2, mx: "auto" }} />
      </Box>
    );
  }

  if (!topData || topData.length === 0) {
    return (
      <Box>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 250 }}>
          <Typography variant="body1" color="text.secondary">
            Nenhum dado disponível
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography 
        variant="h6" 
        component="h3" 
        sx={{ 
          fontSize: "1rem", 
          fontWeight: 600, 
          mb: 2,
          color: "text.primary"
        }}
      >
        {title}
      </Typography>
      
      <Box sx={{ width: "100%", height: 350 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={topData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderLabel}
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
            >
              {topData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value, entry: any) => {
                const item = topData.find((d) => d.name === entry.payload.name);
                return `${value} (${item?.value || 0})`;
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

